import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAdminPTO } from "@hooks/useAdminPTO";
import { useBlockedDays } from "@hooks/useBlockedDays";
import { useCalendarView } from "@hooks/useCalendarView";
import { useAuth } from "@components/context/AuthContext";
import { PTOFilters } from "@components/admin_pto/PTOFilters";
import { PTOTableView } from "@components/admin_pto/PTOTableView";
import { PTOTableViewMobile } from "@components/admin_pto/PTOTableViewMobile";
import { PTOCalendarView } from "@components/admin_pto/PTOCalendarView";
import { PTORequestDrawer } from "@components/admin_pto/PTORequestDrawer";
import { BlockedDaysSection } from "@components/admin_pto/BlockedDaysSection";
import { FiX } from "react-icons/fi";

export default function AdminPTOPage() {
  const { getInfo } = useAuth();
  const [plants, setPlants] = useState<any[]>([]);
  const [userType, setUserType] = useState<string>("");
  const [plantFilter, setPlantFilter] = useState("0");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewMode, setViewMode] = useState("calendar");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeReq, setActiveReq] = useState<any>(null);
  const [calendarMode, setCalendarMode] = useState("month");
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarModalAnimIn, setCalendarModalAnimIn] = useState(false);
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [highlightedRequestId, setHighlightedRequestId] = useState<
    number | null
  >(null);

  // Clamp modal position so the header bar always stays reachable on screen
  const modalRef = useRef<HTMLDivElement>(null);
  const clampPos = useCallback((pos: { x: number; y: number }) => {
    const el = modalRef.current;
    if (!el) return pos;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 40; // keep at least 40px of the modal visible on each edge

    // COORDINATE SYSTEM:
    // - The modal is positioned using CSS: transform: translate(pos.x, pos.y)
    // - rect.getBoundingClientRect() gives the CURRENT screen position (after transform applied)
    // - To find the "base" position (where modal sits at translate(0,0)), we reverse the transform:
    //   baseLeft = rect.left - pos.x  (current left position minus the x offset we applied)
    //   baseTop = rect.top - pos.y    (current top position minus the y offset we applied)
    const baseLeft = rect.left - pos.x;
    const baseTop = rect.top - pos.y;

    // CLAMPING BOUNDARIES:
    // With a new pos, the modal edges would be positioned at:
    //   - Left edge:   baseLeft + pos.x
    //   - Right edge:  baseLeft + rect.width + pos.x
    //   - Top edge:    baseTop + pos.y
    //   - Bottom edge: baseTop + rect.height + pos.y
    //
    // We want to ensure at least 'margin' pixels remain visible:
    //   - Right edge must stay >= margin (from left side of viewport)
    //   - Left edge must stay <= vw - margin (from right side of viewport)
    //   - Bottom edge must stay >= margin (from top of viewport)
    //   - Top edge must stay <= vh - margin (from bottom of viewport)

    // Calculate min/max X: (horizontal clamping)
    // minX: smallest pos.x where right edge stays at margin from left
    //   baseLeft + rect.width + pos.x >= margin
    //   pos.x >= margin - (baseLeft + rect.width)
    const minX = margin - (baseLeft + rect.width);

    // maxX: largest pos.x where left edge stays at vw - margin
    //   baseLeft + pos.x <= vw - margin
    //   pos.x <= vw - margin - baseLeft
    const maxX = vw - margin - baseLeft;

    // Calculate min/max Y: (vertical clamping)
    // minY: smallest pos.y where bottom edge stays at margin from top
    const minY = margin - (baseTop + rect.height);

    // maxY: largest pos.y where top edge stays at vh - margin
    const maxY = vh - margin - baseTop;

    return {
      x: Math.max(minX, Math.min(pos.x, maxX)),
      y: Math.max(minY, Math.min(pos.y, maxY)),
    };
  }, []);

  // Drag-to-move state for calendar modal (persisted in localStorage)
  const [modalPos, setModalPos] = useState(() => {
    try {
      const saved = localStorage.getItem("pto-calendar-modal-pos");
      if (saved) return clampPos(JSON.parse(saved));
    } catch {}
    return { x: 0, y: 0 };
  });
  const dragRef = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  }>({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      const el = modalRef.current;
      const rect = el?.getBoundingClientRect();

      // COORDINATE SYSTEM SETUP:
      // Capture the base position once at drag start. This is where the modal would sit
      // at translate(0, 0) - i.e., its natural position without any transform applied.
      // We calculate this by taking the current screen position and subtracting the
      // current transform offset (modalPos.x, modalPos.y).
      const baseLeft = rect ? rect.left - modalPos.x : 0;
      const baseTop = rect ? rect.top - modalPos.y : 0;
      const modalW = rect?.width ?? 0;

      dragRef.current = {
        dragging: true,
        startX: e.clientX,
        startY: e.clientY,
        origX: modalPos.x,
        origY: modalPos.y,
      };
      let lastPos = { x: modalPos.x, y: modalPos.y };
      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current.dragging) return;

        // Calculate raw position based on mouse movement delta
        const rawX =
          dragRef.current.origX + (ev.clientX - dragRef.current.startX);
        const rawY =
          dragRef.current.origY + (ev.clientY - dragRef.current.startY);

        // CLAMPING LOGIC:
        // The modal position is applied as: transform: translate(pos.x, pos.y)
        // So the actual screen coordinates are: baseLeft + pos.x, baseTop + pos.y
        // We need to keep enough of the modal visible to be draggable and accessible.
        const margin = 240; // horizontal: keep this many pixels visible on each side
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Horizontal clamping - keep 'margin' pixels of modal visible:
        // minX: ensures right edge stays at least 'margin' pixels from left viewport edge
        //   Right edge position: baseLeft + modalW + pos.x
        //   Constraint: baseLeft + modalW + pos.x >= margin
        //   Therefore: pos.x >= margin - (baseLeft + modalW)
        const minX = margin - (baseLeft + modalW);

        // maxX: ensures left edge stays at least 'margin' pixels from right viewport edge
        //   Left edge position: baseLeft + pos.x
        //   Constraint: baseLeft + pos.x <= vw - margin
        //   Therefore: pos.x <= vw - margin - baseLeft
        const maxX = vw - margin - baseLeft;

        // Vertical clamping - keep drag handle (top 50px) within viewport:
        // minY: ensures top edge doesn't go above viewport top
        //   Top edge position: baseTop + pos.y
        //   Constraint: baseTop + pos.y >= 0
        //   Therefore: pos.y >= -baseTop
        const minY = -baseTop;

        // maxY: ensures drag handle (top ~50px of modal) stays above viewport bottom
        //   Top edge position: baseTop + pos.y
        //   Constraint: baseTop + pos.y <= vh - 50
        //   Therefore: pos.y <= vh - 50 - baseTop
        const maxY = vh - 50 - baseTop;

        lastPos = {
          x: Math.max(minX, Math.min(rawX, maxX)),
          y: Math.max(minY, Math.min(rawY, maxY)),
        };
        setModalPos(lastPos);
      };
      const onUp = () => {
        dragRef.current.dragging = false;
        try {
          localStorage.setItem(
            "pto-calendar-modal-pos",
            JSON.stringify(lastPos)
          );
        } catch {}
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [modalPos.x, modalPos.y]
  );
  const [searchVal, setSearchVal] = useState("");
  const breakpoint = 768; // Define the breakpoint for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  // Request Period Filter State
  const [requestPeriodEnabled, setRequestPeriodEnabled] = useState(true);
  const currentYear = new Date().getFullYear();
  const [requestPeriodStart, setRequestPeriodStart] = useState(
    `${currentYear}-01-01`
  );
  const [requestPeriodEnd, setRequestPeriodEnd] = useState(
    `${currentYear}-12-31`
  );

  // PTO Dates Filter State
  const [ptoDatesEnabled, setPtoDatesEnabled] = useState(false);
  const [ptoDatesStart, setPtoDatesStart] = useState(`${currentYear}-01-01`);
  const [ptoDatesEnd, setPtoDatesEnd] = useState(`${currentYear}-12-31`);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  useEffect(() => {
    async function fetchPlants() {
      const result = await getInfo();
      if (result.plants) {
        setPlants(result.plants);
      }
      if (result.userType) {
        setUserType(result.userType);
      }
    }
    fetchPlants();
  }, [getInfo]);

  useEffect(() => {
    if (!calendarModalOpen) {
      setCalendarModalAnimIn(false);
      return;
    }

    try {
      const saved = localStorage.getItem("pto-calendar-modal-pos");
      setModalPos(saved ? clampPos(JSON.parse(saved)) : { x: 0, y: 0 });
    } catch {
      setModalPos({ x: 0, y: 0 });
    }

    const rafId = window.requestAnimationFrame(() => {
      setCalendarModalAnimIn(true);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [calendarModalOpen, clampPos]);

  const {
    requests,
    isLoading,
    error: ptoError,
    setError: setPTOError,
    approveMutation,
    rejectMutation,
    cancelMutation,
    handleReject,
  } = useAdminPTO(
    statusFilter,
    plantFilter,
    plants,
    requestPeriodEnabled,
    requestPeriodStart,
    requestPeriodEnd,
    ptoDatesEnabled,
    ptoDatesStart,
    ptoDatesEnd
  );

  const {
    blockedDays,
    newBlocked,
    setNewBlocked,
    error: blockedError,
    setError: setBlockedError,
    addBlockedMutation,
    deleteBlockedMutation,
  } = useBlockedDays();

  // Filter requests based on search value
  const filteredRequests = useMemo(() => {
    if (!searchVal || !requests) return requests || [];

    const lowerSearch = searchVal.toLowerCase();
    return requests.filter((r: any) => {
      const contractorName = (r.contractor_name || "").toLowerCase();
      const userEmail = (r.user_email || "").toLowerCase();
      const userId = String(r.user_id || "").toLowerCase();
      const status = (r.status || "").toLowerCase();
      const requestType = (r.request_type || "").toLowerCase();
      const description = (r.description || "").toLowerCase();
      const statusReason = (r.status_reason || "").toLowerCase();

      return (
        contractorName.includes(lowerSearch) ||
        userEmail.includes(lowerSearch) ||
        userId.includes(lowerSearch) ||
        status.includes(lowerSearch) ||
        requestType.includes(lowerSearch) ||
        description.includes(lowerSearch) ||
        statusReason.includes(lowerSearch)
      );
    });
  }, [requests, searchVal]);

  const {
    currentMonth,
    currentWeekStart,
    monthMatrix,
    weekDays,
    dayEvents,
    handlePrevMonth,
    handleNextMonth,
    handlePrevWeek,
    handleNextWeek,
    jumpToToday,
    jumpToDate,
  } = useCalendarView(filteredRequests);

  const error = ptoError || blockedError;
  const setError = (err: string | null) => {
    setPTOError(err);
    setBlockedError(err);
  };

  const handleRequestClick = (request: any) => {
    setActiveReq(request);
    setDrawerOpen(true);
  };

  const handleAddBlocked = () => {
    if (!newBlocked.date) return setError("Pick a date to block");
    addBlockedMutation.mutate(newBlocked);
  };

  const handleDeleteBlocked = (id: number) => {
    deleteBlockedMutation.mutate({ id });
  };

  const handleTableRowDoubleClick = (startDate: string, requestId?: number) => {
    if (!startDate) return;
    jumpToDate(startDate);
    setHighlightedRequestId(requestId ?? null);
    setCalendarModalOpen(true);
  };

  const handleCalendarTableRowDoubleClick = (
    startDate: string,
    requestId?: number
  ) => {
    if (!startDate) return;
    jumpToDate(startDate);
    setHighlightedRequestId(requestId ?? null);
  };
  return (
    <div className="tw-p-6 tw-max-w-[1450px] tw-mx-auto">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <h1 className="tw-text-2xl tw-font-semibold">PTO Admin</h1>
      </div>

      {error && (
        <div className="tw-bg-red-50 tw-text-red-700 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-mb-4">
          {error}
        </div>
      )}
      <PTOFilters
        plants={plants}
        plantFilter={plantFilter}
        onPlantChange={setPlantFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        viewMode={viewMode}
        onViewModeChange={(v) => {
          setViewMode(v);
        }}
        onOpenBlockedModal={() => setBlockedModalOpen(true)}
        searchVal={searchVal}
        onSearchChange={setSearchVal}
        isMobile={isMobile}
        requestPeriodEnabled={requestPeriodEnabled}
        onRequestPeriodEnabledChange={setRequestPeriodEnabled}
        requestPeriodStart={requestPeriodStart}
        onRequestPeriodStartChange={setRequestPeriodStart}
        requestPeriodEnd={requestPeriodEnd}
        onRequestPeriodEndChange={setRequestPeriodEnd}
        ptoDatesEnabled={ptoDatesEnabled}
        onPtoDatesEnabledChange={setPtoDatesEnabled}
        ptoDatesStart={ptoDatesStart}
        onPtoDatesStartChange={setPtoDatesStart}
        ptoDatesEnd={ptoDatesEnd}
        onPtoDatesEndChange={setPtoDatesEnd}
      />

      <div className={viewMode === "table" ? "tw-block" : "tw-hidden"}>
        {!isMobile ? (
          <PTOTableView
            requests={requests || []}
            isLoading={isLoading}
            approveMutation={approveMutation}
            handleReject={handleReject}
            cancelMutation={cancelMutation}
            searchVal={searchVal}
            userType={userType}
            onRowDoubleClickDate={handleTableRowDoubleClick}
          />
        ) : (
          <PTOTableViewMobile
            requests={requests || []}
            isLoading={isLoading}
            approveMutation={approveMutation}
            handleReject={handleReject}
            cancelMutation={cancelMutation}
            searchVal={searchVal}
            userType={userType}
          />
        )}
      </div>

      <div className={viewMode === "calendar" ? "tw-block" : "tw-hidden"}>
        <PTOCalendarView
          currentMonth={currentMonth}
          monthMatrix={monthMatrix}
          dayEvents={dayEvents}
          handlePrevMonth={handlePrevMonth}
          handleNextMonth={handleNextMonth}
          onRequestClick={handleRequestClick}
          currentWeekStart={currentWeekStart}
          weekDays={weekDays}
          handlePrevWeek={handlePrevWeek}
          handleNextWeek={handleNextWeek}
          mode={calendarMode}
          onModeChange={setCalendarMode}
          onJumpToToday={jumpToToday}
          plantFilter={plantFilter}
          statusFilter={statusFilter}
          requests={requests || []}
          isLoading={isLoading}
          approveMutation={approveMutation}
          handleReject={handleReject}
          cancelMutation={cancelMutation}
          userType={userType}
          searchVal={searchVal}
          onRowDoubleClickDate={handleCalendarTableRowDoubleClick}
          highlightedRequestId={highlightedRequestId}
          onHighlightRequest={setHighlightedRequestId}
        />
      </div>

      {calendarModalOpen && (
        <div
          className="tw-fixed tw-inset-0 tw-z-40 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/10"
          onClick={() => {
            setCalendarModalOpen(false);
            setHighlightedRequestId(null);
          }}
        >
          <div
            ref={modalRef}
            className={`tw-w-full tw-max-w-6xl tw-max-h-[90vh] tw-overflow-auto tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-xl tw-transition-[opacity] tw-duration-500 tw-ease-out ${
              calendarModalAnimIn ? "tw-opacity-100" : "tw-opacity-0"
            }`}
            style={{
              transform: calendarModalAnimIn
                ? `translate(${modalPos.x}px, ${modalPos.y}px)`
                : `translateX(100%) translate(${modalPos.x}px, ${modalPos.y}px)`,
              transition: dragRef.current.dragging
                ? "opacity 500ms ease-out"
                : "opacity 500ms ease-out, transform 500ms ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 dark:tw-border-gray-700 tw-cursor-grab active:tw-cursor-grabbing"
              onMouseDown={onDragStart}
            >
              <h2 className="tw-text-lg tw-font-semibold tw-select-none">
                PTO Calendar
              </h2>
              <button
                className="tw-text-gray-600 hover:tw-text-black dark:tw-text-gray-300 dark:tw-hover:text-white"
                onClick={() => {
                  setCalendarModalOpen(false);
                  setHighlightedRequestId(null);
                }}
                aria-label="Close calendar modal"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="tw-p-4">
              <PTOCalendarView
                currentMonth={currentMonth}
                monthMatrix={monthMatrix}
                dayEvents={dayEvents}
                handlePrevMonth={handlePrevMonth}
                handleNextMonth={handleNextMonth}
                onRequestClick={handleRequestClick}
                currentWeekStart={currentWeekStart}
                weekDays={weekDays}
                handlePrevWeek={handlePrevWeek}
                handleNextWeek={handleNextWeek}
                mode={calendarMode}
                onModeChange={setCalendarMode}
                onJumpToToday={jumpToToday}
                plantFilter={plantFilter}
                statusFilter={statusFilter}
                highlightedRequestId={highlightedRequestId}
                onHighlightRequest={setHighlightedRequestId}
                requests={requests || []}
                isLoading={isLoading}
                approveMutation={approveMutation}
                handleReject={handleReject}
                cancelMutation={cancelMutation}
                userType={userType}
                searchVal={searchVal}
                onRowDoubleClickDate={handleCalendarTableRowDoubleClick}
              />
            </div>
          </div>
        </div>
      )}

      <PTORequestDrawer
        isOpen={drawerOpen}
        request={activeReq}
        onClose={() => {
          setDrawerOpen(false);
          setActiveReq(null);
        }}
        approveMutation={approveMutation}
        rejectMutation={rejectMutation}
        cancelMutation={cancelMutation}
        userType={userType}
      />

      {blockedModalOpen && (
        <div
          className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/50"
          onClick={() => setBlockedModalOpen(false)}
        >
          <div
            className="tw-w-full tw-max-w-3xl tw-max-h-[90vh] tw-overflow-auto tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 dark:tw-border-gray-700">
              <h2 className="tw-text-lg tw-font-semibold">
                Manage Blocked Days
              </h2>
              <button
                className="tw-text-gray-600 hover:tw-text-black dark:tw-text-gray-300 dark:tw-hover:text-white"
                onClick={() => setBlockedModalOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="tw-p-4">
              <BlockedDaysSection
                blockedDays={blockedDays}
                newBlocked={newBlocked}
                setNewBlocked={setNewBlocked}
                onAddBlocked={handleAddBlocked}
                onDeleteBlocked={handleDeleteBlocked}
                inModal
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
